import { useEffect, useRef, useState } from 'react';
import { Send } from 'lucide-react';
import type { ChatMessage } from '@tutor/shared';
import { Avatar, Button, cn } from '@tutor/ui';
import { formatTime } from '../../lib/format';

export interface ClassroomChatProps {
  messages: ChatMessage[];
  typingUsers: string[];
  currentUserId: string;
  canChat: boolean;
  onSend: (body: string) => void;
  onTyping: (isTyping: boolean) => void;
}

export function ClassroomChat({
  messages,
  typingUsers,
  currentUserId,
  canChat,
  onSend,
  onTyping,
}: ClassroomChatProps) {
  const [draft, setDraft] = useState('');
  const listRef = useRef<HTMLDivElement>(null);
  const typingTimer = useRef<number | null>(null);

  // Keep the newest message in view as the conversation grows.
  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages.length, typingUsers.length]);

  useEffect(
    () => () => {
      if (typingTimer.current) window.clearTimeout(typingTimer.current);
    },
    [],
  );

  const handleChange = (value: string): void => {
    setDraft(value);
    onTyping(true);

    // Typing stops when the keystrokes stop, not when the field is cleared.
    if (typingTimer.current) window.clearTimeout(typingTimer.current);
    typingTimer.current = window.setTimeout(() => onTyping(false), 1_500);
  };

  const submit = (event: React.FormEvent): void => {
    event.preventDefault();
    const body = draft.trim();
    if (!body) return;

    onSend(body);
    setDraft('');
    onTyping(false);
    if (typingTimer.current) window.clearTimeout(typingTimer.current);
  };

  return (
    <div className="flex h-full flex-col">
      <div ref={listRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-3">
        {messages.length === 0 ? (
          <p className="py-8 text-center text-sm text-slate-400">
            No messages yet. Say hello.
          </p>
        ) : (
          messages.map((message) => {
            const isSelf = message.senderId === currentUserId;
            const isSystem = message.type === 'SYSTEM';

            if (isSystem) {
              return (
                <p key={message.id} className="text-center text-xs text-slate-400">
                  {message.body}
                </p>
              );
            }

            return (
              <div key={message.id} className={cn('flex gap-2', isSelf && 'flex-row-reverse')}>
                {!isSelf && (
                  <Avatar name={message.senderName} src={message.senderAvatarUrl} size="xs" className="mt-0.5" />
                )}
                <div className={cn('max-w-[80%]', isSelf && 'items-end text-right')}>
                  {!isSelf && (
                    <p className="mb-0.5 text-xs font-medium text-slate-500">{message.senderName}</p>
                  )}
                  <div
                    className={cn(
                      'inline-block rounded-2xl px-3 py-2 text-sm',
                      isSelf
                        ? 'rounded-br-sm bg-brand-600 text-white'
                        : 'rounded-bl-sm bg-slate-100 text-slate-800',
                    )}
                  >
                    {message.body}
                  </div>
                  <p className="mt-0.5 text-[10px] text-slate-400">{formatTime(message.createdAt)}</p>
                </div>
              </div>
            );
          })
        )}

        {typingUsers.length > 0 && (
          <p className="text-xs italic text-slate-400" aria-live="polite">
            {typingUsers.join(', ')} {typingUsers.length === 1 ? 'is' : 'are'} typing…
          </p>
        )}
      </div>

      <form onSubmit={submit} className="flex items-center gap-2 border-t border-slate-200 p-3">
        <input
          value={draft}
          onChange={(event) => handleChange(event.target.value)}
          disabled={!canChat}
          placeholder={canChat ? 'Send a message…' : 'Chat is read-only'}
          aria-label="Message"
          maxLength={4000}
          className="min-w-0 flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-200 disabled:bg-slate-50"
        />
        <Button type="submit" size="sm" disabled={!canChat || !draft.trim()} aria-label="Send message">
          <Send className="h-4 w-4" aria-hidden />
        </Button>
      </form>
    </div>
  );
}
